import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import ApiClient from '../../utils/ApiClient';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import envConfig from '../../utils/EnvironmentConfigs';

const MIN_SIMULATION_REQUESTS = 1;
const MAX_SIMULATION_REQUESTS = 1000;
const DEFAULT_SIMULATION_REQUESTS = 20;
const SETTING_LABELS = {
    keycloakUrl: 'Keycloak URL',
    keycloakRealm: 'Keycloak Realm',
    keycloakClientId: 'Keycloak Client ID',
    apiBaseUrl: 'API Base URL',
    showBanner: 'Announcement Banner',
    recertificationDays: 'Recertification Days',
    recertificationRemovalDays: 'Recertification Removal Days',
};

const formatSettingLabel = (settingKey) => {
    if (SETTING_LABELS[settingKey]) {
        return SETTING_LABELS[settingKey];
    }

    if (settingKey.includes('_')) {
        return settingKey
            .toLowerCase()
            .split('_')
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');
    }

    return settingKey.replace(/([A-Z])/g, ' $1').replace(/^./, (character) => character.toUpperCase());
};

const sortSettingsEntries = (settings) =>
    Object.entries(settings).sort(([leftKey], [rightKey]) =>
        formatSettingLabel(leftKey).localeCompare(formatSettingLabel(rightKey), undefined, {
            sensitivity: 'base',
        })
    );

const asSettingsObject = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }

    return value;
};

const asBooleanSetting = (settingValue) => {
    if (typeof settingValue === 'boolean') {
        return settingValue;
    }

    if (typeof settingValue === 'string') {
        const normalizedValue = settingValue.trim().toLowerCase();
        if (normalizedValue === 'true') {
            return true;
        }

        if (normalizedValue === 'false') {
            return false;
        }
    }

    return null;
};

const asActivatedStatus = (value) => asBooleanSetting(value) === true;

const AdminPage = ({ authDetails, onSignOut }) => {
    const [simulationRequestCount, setSimulationRequestCount] = useState(DEFAULT_SIMULATION_REQUESTS);
    const [isGeneratingSimulationData, setIsGeneratingSimulationData] = useState(false);
    const [isDeletingSimulationData, setIsDeletingSimulationData] = useState(false);
    const [simulationGenerationError, setSimulationGenerationError] = useState('');
    const [simulationGenerationSuccess, setSimulationGenerationSuccess] = useState('');
    const [e2eModeActivated, setE2eModeActivated] = useState(false);
    const [isLoadingE2eModeStatus, setIsLoadingE2eModeStatus] = useState(true);
    const [isUpdatingE2eModeStatus, setIsUpdatingE2eModeStatus] = useState(false);
    const [e2eModeError, setE2eModeError] = useState('');
    const [e2eModeSuccess, setE2eModeSuccess] = useState('');
    const [backendSettings, setBackendSettings] = useState({});
    const [backendGuides, setBackendGuides] = useState({});
    const [isLoadingBackendSettings, setIsLoadingBackendSettings] = useState(true);
    const [backendSettingsError, setBackendSettingsError] = useState('');
    const sortedFrontendSettingEntries = sortSettingsEntries(envConfig);
    const sortedBackendSettingEntries = sortSettingsEntries(backendSettings);
    const sortedBackendGuideEntries = Object.entries(backendGuides).sort(([leftKey], [rightKey]) =>
        leftKey.localeCompare(rightKey, undefined, { sensitivity: 'base' })
    );

    useEffect(() => {
        let isMounted = true;

        const loadBackendSettings = async () => {
            setIsLoadingBackendSettings(true);
            setBackendSettingsError('');

            try {
                const response = await ApiClient.get('/settings');
                const apiEnvSettings = asSettingsObject(response?.data?.env);
                const apiGuides = asSettingsObject(response?.data?.guides);

                if (isMounted) {
                    setBackendSettings(apiEnvSettings);
                    setBackendGuides(apiGuides);
                }
            } catch (error) {
                const message =
                    error?.response?.data?.message ??
                    'Unable to load backend settings. Please refresh and try again.';

                if (isMounted) {
                    setBackendSettingsError(message);
                    setBackendSettings({});
                    setBackendGuides({});
                }
            } finally {
                if (isMounted) {
                    setIsLoadingBackendSettings(false);
                }
            }
        };

        const loadE2eModeStatus = async () => {
            setIsLoadingE2eModeStatus(true);
            setE2eModeError('');

            try {
                const response = await ApiClient.get('/e2e');
                const activated = asActivatedStatus(response?.data?.activated);

                if (isMounted) {
                    setE2eModeActivated(activated);
                }
            } catch (error) {
                const message =
                    error?.response?.data?.message ??
                    'Unable to load E2E testing mode status. Please refresh and try again.';

                if (isMounted) {
                    setE2eModeError(message);
                    setE2eModeActivated(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingE2eModeStatus(false);
                }
            }
        };

        loadBackendSettings();
        loadE2eModeStatus();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleSimulationRequestCountChange = (event) => {
        const nextValue = Number(event.target.value);
        if (Number.isNaN(nextValue)) {
            return;
        }

        setSimulationRequestCount(nextValue);
    };

    const handleGenerateSimulationData = async () => {
        const isConfirmed = window.confirm(
            'Generate simulation data? This is destructive and should only be used in Development/UAT.'
        );

        if (!isConfirmed) {
            return;
        }

        setIsGeneratingSimulationData(true);
        setSimulationGenerationError('');
        setSimulationGenerationSuccess('');

        try {
            const response = await ApiClient.post('/access-requests/simulation-data', {
                count: simulationRequestCount,
            });
            const generatedCount = Number(response?.data?.generatedCount ?? simulationRequestCount);
            setSimulationGenerationSuccess(
                `Simulation data generated successfully. ${generatedCount} access requests were created.`
            );
        } catch (error) {
            const message =
                error?.response?.data?.message ??
                'Unable to generate simulation data. Please verify environment restrictions and try again.';
            setSimulationGenerationError(message);
        } finally {
            setIsGeneratingSimulationData(false);
        }
    };

    const handleDeleteSimulationData = async () => {
        const isConfirmed = window.confirm(
            'Delete simulation data? This is destructive and should only be used in Development/UAT.'
        );

        if (!isConfirmed) {
            return;
        }

        setIsDeletingSimulationData(true);
        setSimulationGenerationError('');
        setSimulationGenerationSuccess('');

        try {
            const response = await ApiClient.delete('/access-requests/simulation-data');
            const accessRequestsDeleted = Number(response?.data?.accessRequestsDeleted ?? 0);
            const reviewsDeleted = Number(response?.data?.reviewsDeleted ?? 0);
            setSimulationGenerationSuccess(
                `Simulation data deleted successfully. ${accessRequestsDeleted} access requests and ${reviewsDeleted} reviews were removed.`
            );
        } catch (error) {
            const message =
                error?.response?.data?.message ??
                'Unable to delete simulation data. Please verify environment restrictions and try again.';
            setSimulationGenerationError(message);
        } finally {
            setIsDeletingSimulationData(false);
        }
    };

    const handleToggleE2eModeStatus = async () => {
        const isDeactivating = e2eModeActivated;

        setIsUpdatingE2eModeStatus(true);
        setE2eModeError('');
        setE2eModeSuccess('');

        try {
            const response = isDeactivating ? await ApiClient.delete('/e2e') : await ApiClient.post('/e2e');
            const activated = asActivatedStatus(response?.data?.activated);

            setE2eModeActivated(activated);
            setE2eModeSuccess(
                activated ? 'E2E testing mode activated.' : 'E2E testing mode deactivated.'
            );
        } catch (error) {
            const message =
                error?.response?.data?.message ??
                `Unable to ${isDeactivating ? 'deactivate' : 'activate'} E2E testing mode. Please try again.`;
            setE2eModeError(message);
        } finally {
            setIsUpdatingE2eModeStatus(false);
        }
    };

    const renderSettingRow = (sectionKey, settingKey, settingValue) => {
        const settingLabel = formatSettingLabel(settingKey);
        const booleanSettingValue = asBooleanSetting(settingValue);
        const settingInputId = `admin-setting-${sectionKey}-${settingKey
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')}`;

        return (
            <div className="admin-settings__row" key={settingKey}>
                <span className="admin-settings__row-label">{settingLabel}</span>
                {booleanSettingValue !== null ? (
                    <div className="usa-toggle">
                        <input
                            id={settingInputId}
                            className="usa-toggle__input"
                            type="checkbox"
                            checked={booleanSettingValue}
                            aria-label={`${settingLabel}: ${booleanSettingValue ? 'On' : 'Off'}`}
                            readOnly
                            disabled
                        />
                        <label className="usa-toggle__label" htmlFor={settingInputId}>
                            <span className="admin-settings__toggle-state">
                                {booleanSettingValue ? 'On' : 'Off'}
                            </span>
                        </label>
                    </div>
                ) : (
                    <input
                        className="usa-input"
                        type="text"
                        value={String(settingValue ?? '')}
                        aria-label={settingLabel}
                        readOnly
                        disabled
                    />
                )}
            </div>
        );
    };

    const renderGuideRow = (guideKey, guideValue) => {
        const guideLabel = `Guide: ${guideKey}`;

        return (
            <div className="admin-settings__row admin-settings__row--guide" key={guideKey}>
                <span className="admin-settings__row-label">{guideKey}</span>
                <textarea
                    className="usa-textarea admin-settings__guide-textarea"
                    value={String(guideValue ?? '')}
                    aria-label={guideLabel}
                    readOnly
                    disabled
                    rows={8}
                />
            </div>
        );
    };

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="admin-page__intro">
                        <div className="usa-alert usa-alert--info admin-page__info-banner" role="status">
                            <div className="usa-alert__body">
                                <p className="usa-alert__text">
                                    Only members of UNLOCK-Administrators can access this page.
                                </p>
                            </div>
                        </div>
                        <div className="usa-prose">
                            <h1 className="margin-y-0">Administrative Settings</h1>
                        </div>
                    </div>

                    <div className="usa-card margin-top-4">
                        <div className="usa-card__container">
                            <div className="usa-card__header">
                                <h2 className="usa-card__heading">Front End (UI) Settings</h2>
                            </div>
                            <div className="usa-card__body">
                                <div className="admin-settings__list">
                                    {sortedFrontendSettingEntries.map(([settingKey, settingValue]) =>
                                        renderSettingRow('frontend', settingKey, settingValue)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="usa-card margin-top-4">
                        <div className="usa-card__container">
                            <div className="usa-card__header">
                                <h2 className="usa-card__heading">Backend (API) Settings</h2>
                            </div>
                            <div className="usa-card__body">
                                {isLoadingBackendSettings ? (
                                    <div className="admin-settings__list" aria-hidden="true">
                                        {Array.from({ length: 4 }).map((_, index) => (
                                            <p key={`backend-setting-loading-${index}`} className="margin-y-1">
                                                <ValueWithSkeleton loading width="14ch" />{' '}
                                                <ValueWithSkeleton loading type="sentence" width="26ch" />
                                            </p>
                                        ))}
                                    </div>
                                ) : null}

                                {backendSettingsError ? (
                                    <div className="usa-alert usa-alert--error margin-top-2" role="alert">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{backendSettingsError}</p>
                                        </div>
                                    </div>
                                ) : null}

                                {!isLoadingBackendSettings && !backendSettingsError ? (
                                    <>
                                        <h3 className="admin-settings__subheading margin-top-0">
                                            Environment Variables
                                        </h3>
                                        <div className="admin-settings__list">
                                            {sortedBackendSettingEntries.length > 0 ? (
                                                sortedBackendSettingEntries.map(([settingKey, settingValue]) =>
                                                    renderSettingRow('backend', settingKey, settingValue)
                                                )
                                            ) : (
                                                <p className="margin-y-0">No backend settings returned.</p>
                                            )}
                                        </div>

                                        <h3 className="admin-settings__subheading margin-top-3">
                                            Group Access Guides
                                        </h3>
                                        <div className="admin-settings__list">
                                            {sortedBackendGuideEntries.length > 0 ? (
                                                sortedBackendGuideEntries.map(([guideKey, guideValue]) =>
                                                    renderGuideRow(guideKey, guideValue)
                                                )
                                            ) : (
                                                <p className="margin-y-0">No guides returned.</p>
                                            )}
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    <div className="usa-card margin-top-4">
                        <div className="usa-card__container">
                            <div className="usa-card__header">
                                <h2 className="usa-card__heading">E2E Testing Mode</h2>
                            </div>
                            <div className="usa-card__body">
                                <p className="margin-y-0">
                                    <strong>Current status:</strong>{' '}
                                    {isLoadingE2eModeStatus
                                        ? <ValueWithSkeleton loading width="8ch" />
                                        : e2eModeActivated
                                          ? 'Activated'
                                          : 'Not'}
                                </p>

                                {e2eModeError ? (
                                    <div className="usa-alert usa-alert--error margin-top-2" role="alert">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{e2eModeError}</p>
                                        </div>
                                    </div>
                                ) : null}

                                {e2eModeSuccess ? (
                                    <div className="usa-alert usa-alert--success margin-top-2" role="status">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{e2eModeSuccess}</p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div className="usa-card__footer">
                                <button
                                    type="button"
                                    className="usa-button usa-button--secondary"
                                    onClick={handleToggleE2eModeStatus}
                                    disabled={isLoadingE2eModeStatus || isUpdatingE2eModeStatus}
                                >
                                    {isUpdatingE2eModeStatus
                                        ? e2eModeActivated
                                          ? 'Deactivating...'
                                          : 'Activating...'
                                        : e2eModeActivated
                                          ? 'Deactivate E2E testing mode'
                                          : 'Activate E2E testing mode'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="usa-card margin-top-4">
                        <div className="usa-card__container">
                            <div className="usa-card__header">
                                <h2 className="usa-card__heading">Load Simulation Data</h2>
                            </div>
                            <div className="usa-card__body">
                                <div className="usa-alert usa-alert--warning" role="alert">
                                    <div className="usa-alert__body">
                                        <p className="usa-alert__text">
                                            Clicking Generate data is a destructive action. Run this only in
                                            Development and UAT environments for end-to-end and regression testing.
                                        </p>
                                    </div>
                                </div>

                                {simulationGenerationSuccess ? (
                                    <div className="usa-alert usa-alert--success margin-top-2" role="status">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{simulationGenerationSuccess}</p>
                                        </div>
                                    </div>
                                ) : null}

                                {simulationGenerationError ? (
                                    <div className="usa-alert usa-alert--error margin-top-2" role="alert">
                                        <div className="usa-alert__body">
                                            <p className="usa-alert__text">{simulationGenerationError}</p>
                                        </div>
                                    </div>
                                ) : null}

                                <label className="usa-label margin-top-3" htmlFor="simulation-request-count">
                                    Access requests to generate: {simulationRequestCount}
                                </label>
                                <input
                                    id="simulation-request-count"
                                    className="usa-range"
                                    type="range"
                                    min={MIN_SIMULATION_REQUESTS}
                                    max={MAX_SIMULATION_REQUESTS}
                                    step="1"
                                    value={simulationRequestCount}
                                    onChange={handleSimulationRequestCountChange}
                                />
                            </div>
                            <div className="usa-card__footer">
                                <ul className="usa-button-group">
                                    <li className="usa-button-group__item">
                                        <button
                                            type="button"
                                            className="usa-button usa-button--secondary"
                                            onClick={handleGenerateSimulationData}
                                            disabled={isGeneratingSimulationData || isDeletingSimulationData}
                                        >
                                            {isGeneratingSimulationData ? 'Generating data...' : 'Generate data'}
                                        </button>
                                    </li>
                                    <li className="usa-button-group__item">
                                        <button
                                            type="button"
                                            className="usa-button usa-button--outline"
                                            onClick={handleDeleteSimulationData}
                                            disabled={isGeneratingSimulationData || isDeletingSimulationData}
                                        >
                                            {isDeletingSimulationData ? 'Deleting data...' : 'Delete simulation data'}
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="margin-top-4">
                        <Link to="/home" className="usa-button usa-button--outline">
                            Back to home
                        </Link>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default AdminPage;
